import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Check, Users, Flame } from "lucide-react";
import { BETA } from "../mock";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  firstName: "",
  email: "",
  device: "",
  status: "",
  relationship: "",
  willing: false,
  consent: false,
};

const BetaSection = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null); // { count, spots_left, capacity, round }
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/beta/count`);
      setMeta(data);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) return toast({ title: "Please enter your first name", variant: "destructive" });
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return toast({ title: "Please enter a valid email", variant: "destructive" });
    if (!form.device) return toast({ title: "Please choose your device type", variant: "destructive" });
    if (!form.status) return toast({ title: "Please choose your status", variant: "destructive" });
    if (!form.relationship) return toast({ title: "Please choose your relationship to the military", variant: "destructive" });
    if (!form.willing) return toast({ title: "Please confirm you can test for 21 days", variant: "destructive" });
    if (!form.consent) return toast({ title: "Please consent to beta communication", variant: "destructive" });

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/beta/apply`, form);
      if (data?.waitlisted || isFull) {
        toast({ title: "You're on the waitlist", description: "This round is full \u2014 we'll email you the moment a spot opens up." });
      } else {
        toast({ title: "Application received", description: "Thanks for applying \u2014 check your inbox for a confirmation email." });
      }
      setForm(initial);
      fetchCount();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast({ title: "You're already on the list", description: "This email has already applied. We'll be in touch." });
      } else {
        toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showNudge = meta && typeof meta.spots_left === "number" && meta.spots_left > 0;
  const isFull = meta && typeof meta.spots_left === "number" && meta.spots_left <= 0;
  const ctaLabel = isFull ? "Join the Waitlist" : BETA.cta;

  return (
    <section id="beta" className="relative py-20 lg:py-28 bg-[#0A1220]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="text-[13px] font-semibold tracking-wide text-[#13D99A] uppercase mb-4">
            {isFull ? "Waitlist" : "Private beta"}
          </p>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#F5F7FB] leading-[1.08]">
            {isFull ? "This round is full \u2014 join the waitlist." : BETA.headline}
          </h2>
          <p className="mt-5 text-lg text-[#8290A8] leading-relaxed">
            {isFull
              ? "Every spot in this round has been claimed. Join the waitlist and we'll email you the moment a spot opens up \u2014 invites go out in the order people joined."
              : BETA.copy}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {meta && meta.count > 0 && (
              <div className="inline-flex items-center gap-2.5 rounded-full border border-[#10C98A]/40 bg-[#10C98A]/10 px-4 py-2">
                <Users className="w-4 h-4 text-[#13D99A]" />
                <span className="text-sm text-[#F5F7FB]">
                  <span className="font-semibold text-[#13D99A]">{meta.count.toLocaleString()}</span>{" "}
                  {meta.count === 1 ? "person has" : "people have"} joined the beta
                </span>
              </div>
            )}
            {showNudge && (
              <div className="inline-flex items-center gap-2.5 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-2">
                <Flame className="w-4 h-4 text-[#F5A623]" />
                <span className="text-sm text-[#F5F7FB]">
                  Only <span className="font-semibold text-[#F5A623]">{meta.spots_left.toLocaleString()}</span> spots left this {meta.round}
                </span>
              </div>
            )}
            {isFull && (
              <div className="inline-flex items-center gap-2.5 rounded-full border border-[#FF4D4F]/40 bg-[#FF4D4F]/10 px-4 py-2">
                <Flame className="w-4 h-4 text-[#FF4D4F]" />
                <span className="text-sm text-[#F5F7FB]">
                  This {meta.round} is <span className="font-semibold text-[#FF4D4F]">full</span> — waitlist open
                </span>
              </div>
            )}
          </div>

          <ul className="mt-8 space-y-3">
            {BETA.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#F5F7FB]">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#10C98A]/20 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-[#13D99A]" />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={100}>
          <form onSubmit={submit} className="rounded-2xl border border-[#29354A] bg-[#121A28] p-6 lg:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-[#F5F7FB]">First name</Label>
                <Input
                  id="firstName"
                  value={form.firstName}
                  onChange={(e) => set("firstName", e.target.value)}
                  placeholder="Your first name"
                  className="bg-[#0A1220] border-[#29354A] text-[#F5F7FB] placeholder:text-[#8290A8]/60"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#F5F7FB]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#0A1220] border-[#29354A] text-[#F5F7FB] placeholder:text-[#8290A8]/60"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#F5F7FB]">Device type</Label>
              <Select value={form.device} onValueChange={(v) => set("device", v)}>
                <SelectTrigger className="bg-[#0A1220] border-[#29354A] text-[#F5F7FB]">
                  <SelectValue placeholder="Select your device" />
                </SelectTrigger>
                <SelectContent className="bg-[#121A28] border-[#29354A] text-[#F5F7FB]">
                  <SelectItem value="iphone">iPhone</SelectItem>
                  <SelectItem value="ipad">iPad</SelectItem>
                  <SelectItem value="both">iPhone &amp; iPad</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#F5F7FB]">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="bg-[#0A1220] border-[#29354A] text-[#F5F7FB]">
                    <SelectValue placeholder="Military or civilian" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121A28] border-[#29354A] text-[#F5F7FB]">
                    <SelectItem value="military">Military</SelectItem>
                    <SelectItem value="civilian">Civilian</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#F5F7FB]">Relationship to military</Label>
                <Select value={form.relationship} onValueChange={(v) => set("relationship", v)}>
                  <SelectTrigger className="bg-[#0A1220] border-[#29354A] text-[#F5F7FB]">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121A28] border-[#29354A] text-[#F5F7FB]">
                    <SelectItem value="active">Active duty</SelectItem>
                    <SelectItem value="reserve">Reserve</SelectItem>
                    <SelectItem value="guard">National Guard</SelectItem>
                    <SelectItem value="veteran">Veteran</SelectItem>
                    <SelectItem value="spouse">Military spouse</SelectItem>
                    <SelectItem value="transitioning">Transitioning service member</SelectItem>
                    <SelectItem value="none">No military affiliation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.willing} onCheckedChange={(v) => set("willing", !!v)} className="mt-0.5 border-[#29354A] data-[state=checked]:bg-[#10C98A] data-[state=checked]:border-[#10C98A]" />
              <span className="text-sm text-[#8290A8]">I'm willing to test Renn for at least 21 days and share feedback.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-[#29354A] data-[state=checked]:bg-[#10C98A] data-[state=checked]:border-[#10C98A]" />
              <span className="text-sm text-[#8290A8]">I consent to receive beta communication from Renn.</span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-emerald w-full bg-[#10C98A] text-[#050A12] font-semibold py-4 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting\u2026" : ctaLabel}
            </button>
            <p className="text-xs text-[#8290A8]/70 text-center">We do not collect financial information through this form.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default BetaSection;
